export function findSiteObjectInViewerScene(scene, siteUuid) {
  if (!scene || !siteUuid) return null;
  const stack = [scene];
  const seen = new Set();
  while (stack.length > 0) {
    const obj = stack.pop();
    if (!obj || seen.has(obj)) continue;
    seen.add(obj);
    if (obj.userData?.type === "site" && obj.userData?.siteUuid === siteUuid) {
      return obj;
    }
    const children = obj.children;
    if (children && children.length > 0) {
      for (let i = children.length - 1; i >= 0; i -= 1) {
        stack.push(children[i]);
      }
    }
  }
  return null;
}

export function findGeomObjectInViewerScene(scene, geomRef) {
  if (!scene || !geomRef) return null;
  const target = String(geomRef);
  const stack = [scene];
  const seen = new Set();
  while (stack.length > 0) {
    const obj = stack.pop();
    if (!obj || seen.has(obj)) continue;
    seen.add(obj);
    const ud = obj.userData || {};
    if (
      (ud.geomUuid && String(ud.geomUuid) === target) ||
      (ud.geomName && String(ud.geomName) === target)
    ) {
      return obj;
    }
    const children = obj.children;
    if (children && children.length > 0) {
      for (let i = children.length - 1; i >= 0; i -= 1) {
        stack.push(children[i]);
      }
    }
  }
  return null;
}

export function buildViewerSceneRouteObjectIndex(scene) {
  const sites = new Map();
  const geoms = new Map();
  if (!scene) return { sites, geoms };

  const addRef = (map, ref, obj) => {
    if (ref == null || ref === "") return;
    const key = String(ref);
    if (!map.has(key)) map.set(key, obj);
  };

  scene.traverse((obj) => {
    const ud = obj.userData || {};
    if (ud.type === "site") {
      addRef(sites, ud.siteUuid, obj);
      addRef(sites, ud.siteName, obj);
    }
    addRef(geoms, ud.geomUuid, obj);
    addRef(geoms, ud.geomName, obj);
  });

  return { sites, geoms };
}

export function collectSpatialTendonRouteObjects(scene, tendon, routeObjectIndex = null) {
  if (!scene || tendon?.kind !== "spatial" || !Array.isArray(tendon.spatial)) return [];
  const index = routeObjectIndex || buildViewerSceneRouteObjectIndex(scene);
  return tendon.spatial
    .map((entry) => {
      if (entry?.kind === "site" && entry.site) {
        return index.sites.get(String(entry.site)) || null;
      }
      if (entry?.kind === "geom") {
        if (entry.sidesite) {
          return index.sites.get(String(entry.sidesite)) || null;
        }
        return index.geoms.get(String(entry.geom)) || null;
      }
      return null;
    })
    .filter(Boolean);
}

export function buildSpatialTendonWrapGeomRefSet(
  tendons = {},
  {
    tendonMarkerLayerVisible = false,
    selectedTendon = null,
    tendonMarkerVisibility = {},
    focusSelectedOnly = false,
  } = {}
) {
  const refs = new Set();
  Object.values(tendons || {}).forEach((tendon) => {
    if (!tendon || tendon.kind !== "spatial" || !Array.isArray(tendon.spatial)) return;
    const tendonUuid = tendon.uuid != null ? String(tendon.uuid) : "";
    if (tendonUuid && (tendonMarkerVisibility[tendonUuid] ?? true) === false) return;
    const selected = tendonUuid && selectedTendon != null && tendonUuid === String(selectedTendon);
    const active = focusSelectedOnly ? selected : tendonMarkerLayerVisible || selected;
    if (!active) return;
    tendon.spatial.forEach((entry) => {
      if (entry?.kind !== "geom" || !entry.geom) return;
      refs.add(String(entry.geom));
    });
  });
  return refs;
}

/** Matches `selectionStore` inertial keys (`bodyUuid:id`, see getInertialTarget). */
export function getInertialSelectionKeyFromUserData(ud) {
  if (!ud || ud.type !== "inertial") return null;
  const bodyUuid = ud.bodyUuid || null;
  const id = ud.inertialUuid || ud.inertialName;
  if (bodyUuid && id) return `${bodyUuid}:${id}`;
  if (id) return String(id);
  return null;
}

// Three.js's built-in Object3D.traverse() is recursive and can overflow the call stack
// if the scene graph accidentally contains a cycle. Use an iterative, cycle-safe traversal
// to keep the app responsive even if the hierarchy is temporarily corrupted.
export function safeTraverse(root, visitor) {
  if (!root || typeof visitor !== "function") return;
  const stack = [root];
  const seen = new Set();
  while (stack.length > 0) {
    const obj = stack.pop();
    if (!obj || seen.has(obj)) continue;
    seen.add(obj);
    // If visitor returns false, stop traversal early (useful for "find first" patterns).
    const res = visitor(obj);
    if (res === false) return;
    const children = obj.children;
    if (Array.isArray(children) && children.length > 0) {
      for (let i = children.length - 1; i >= 0; i--) stack.push(children[i]);
    }
  }
}

export function findJointVizGroupInScene(scene, jointIdentifier) {
  if (!scene || !jointIdentifier) return null;
  let found = null;
  safeTraverse(scene, (obj) => {
    if (found) return false;
    if (
      obj.userData &&
      obj.userData.isJointVizGroup &&
      obj.userData.jointUuid === jointIdentifier
    ) {
      found = obj;
      return false;
    }
  });
  return found;
}
